import React, { ReactNode } from 'react';
import { useArticleProps } from '../../hooks/content/useArticleProps';
import { useStackVideoPlayerContext } from '@jbhi-fi/components/src/hooks/useStackVideoPlayer';
import { HeadingDivider, HeadingDividerVariant } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { styles } from './TimelineWrapper.css';

const secondsToMinutesAndSeconds = (seconds: number): { minutes: string; seconds: string } => {
  return { minutes: String(Math.floor(seconds / 60)).padStart(1, '0'), seconds: String(seconds % 60).padStart(2, '0') };
};

export type TimelineWrapperProps = { heading?: string; children: ReactNode };

export type TimelineItemProps = {
  heading: string;
  timestamp: number;
  children: ReactNode;
};

export const TimelineItem = ({ heading, timestamp, children }: TimelineItemProps): JSX.Element => {
  const { seconds, minutes } = secondsToMinutesAndSeconds(timestamp);
  const { data: { articleHeader } = {} } = useArticleProps();
  const { players } = useStackVideoPlayerContext();

  const handleTimestampSeek = (event: React.MouseEvent<HTMLDivElement>, time: number) => {
    event.preventDefault();
    const player = articleHeader?.videoSrc ? players?.get(articleHeader.videoSrc) : undefined;
    if (player) {
      player?.seek(time);
      player?.play();
      window.scrollTo(0, 0);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar') {
      event.currentTarget.click();
    }
  };

  return (
    <div className={styles.itemWrapper}>
      <div className={styles.timeline}>
        <div className={styles.circle} />
        <div className={styles.line} />
      </div>
      <div
        className={styles.headingContainer}
        role="button"
        tabIndex={0}
        aria-pressed="false"
        onKeyDown={handleKeyDown}
        title={`Jump to ${minutes}:${seconds} in video`}
        onClick={e => handleTimestampSeek(e, timestamp)}
      >
        <div className={styles.heading}>{heading}</div>
        <div className={styles.timestamp}>{`${minutes}:${seconds}`}</div>
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );
};

export const TimelineWrapper = ({ children, heading }: TimelineWrapperProps): JSX.Element => {
  return (
    <div className={styles.wrapper}>
      {heading ? (
        <div className={styles.wrapperHeading}>
          <HeadingDivider heading={heading} variant={HeadingDividerVariant.small} />
        </div>
      ) : null}
      {children}
    </div>
  );
};
